import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { DateService } from '@application/time/date.service';
import { Presentation } from '@visiba/presentation';
import { CaseModelShared, Issues } from './case_model_shared';
import { Injectable, Scopes } from '@visiba/instantiation';
import { MessagingConversationsApiService } from '@api_patient/messaging_conversations_api.service';
import type { Participant } from './common';
import type { CaseOtherApiModel } from '@api_patient/generated/clientv2/models';

export interface MessagingData {
	readonly id: string;
	readonly completed: boolean;
	readonly date: VisibaDate;
	readonly participants: Readonly<Participant>[];
	readonly receptionName: string;
	readonly issues: Issues;
	readonly rfv: Nullable<string>;
}

export interface MessagingMetaData {
	readonly hasReadUntilTimestamp: VisibaDate | undefined;
	readonly lastMessageTimestamp: VisibaDate | undefined;
	readonly allowClientResponse: boolean;
	readonly completedTimestamp: VisibaDate | undefined;
	readonly receiptUrl: Nullable<string>;
}

interface Presentation {
	readonly data: MessagingData | null;
	readonly metaData: MessagingMetaData | null;
}

@Injectable({
	scope: Scopes.Transient,
})
export class MessagingCaseModel extends CaseModelShared<MessagingData> {
	public static readonly MESSAGE_UNHANDLED_ISSUES_TRACKING_ID_PREFIX = 'M_';
	public readonly presentation = Presentation.create<Presentation>({
		data: null,
		metaData: null,
	});

	constructor(
		private readonly dateService: DateService,
		private readonly messagingConversationsApiService: MessagingConversationsApiService,
		private readonly analyticService: AnalyticService,
	) {
		super();
	}

	public updateLesserData(caseData: CaseOtherApiModel): void {
		if (caseData.MessageConversationId == null) throw new Error('Invalid messaging');

		const participants: Participant[] = [];
		for (const participant of caseData.OtherParticipants) {
			participants.push({
				descriptiveTitle: participant.DescriptiveTitle,
				title: participant.Title,
				leftTimestamp: participant.LeftTimestamp,
				// TODO PAT-426: Allow null?
				name: participant.Name ?? '',
				profileImageUrl: participant.ProfileImageUrl,
			});
		}

		this.presentation.write({
			data: {
				id: caseData.MessageConversationId.toString(),
				completed: caseData.Completed,
				date: this.dateService.newDate(caseData.Time),
				participants: participants,
				receptionName: caseData.ReceptionName ?? '',
				issues: this.parseIssues(caseData),
				rfv: caseData.Condition,
			},
		});
	}

	public get(): MessagingData {
		const data = this.presentation.read().data;
		if (data == null) throw new Error('Did not expect data to be null');

		return data;
	}

	public updateExtendedData(): void {
		this.messagingConversationsApiService.getMessagingConversation(this.getIdentifier()).then((result) => {
			if (result.err) return;

			const data = result.ok.data;
			const lastMessageTimestamp =
				// TODO PAT-426: Missing type? LastMessageTimestamp
				(data as unknown as { LastMessageTimestamp: string }).LastMessageTimestamp != null
					? // TODO PAT-426: Missing type? LastMessageTimestamp
						this.dateService.newDate((data as unknown as { LastMessageTimestamp: string }).LastMessageTimestamp)
					: undefined;
			const hasReadUntilTimestamp =
				data.HasReadUntilTimestamp != null ? this.dateService.newDate(data.HasReadUntilTimestamp) : undefined;
			const completedTimestamp = data.CompletedTimestamp != null ? this.dateService.newDate(data.CompletedTimestamp) : undefined;

			this.presentation.write({
				metaData: {
					lastMessageTimestamp: lastMessageTimestamp,
					hasReadUntilTimestamp: hasReadUntilTimestamp,
					completedTimestamp: completedTimestamp,
					allowClientResponse: data.AllowClientResponse,
					receiptUrl: data.PaymentInfo?.ReceiptUrl ?? null,
				},
			});
		});
	}

	public getIdentifier(): string {
		const data = this.presentation.read().data;
		if (data?.id == null) throw new Error('data not set');

		return data.id;
	}

	public markAsCompleted(date: VisibaDate): void {
		this.presentation.write((data) => {
			const prev = data.extract();
			if (prev.metaData == null) return prev;

			return {
				metaData: {
					...prev.metaData,
					completedTimestamp: date,
				},
			};
		});
	}

	public markAsRead(): void {
		this.presentation.write((data) => {
			const prev = data.extract();
			if (prev.data == null) return prev;

			const issues = prev.data.issues as Issues;

			return {
				data: {
					...prev.data,
					issues: {
						...issues,
						unreadMessages: 0,
						totalCount: issues.totalCount - issues.unreadMessages,
					} as Issues,
				},
			};
		});
	}

	public analyticQuirk_trackOnMountInVisitsView(): void {
		const data = this.get();

		this.analyticService.trackEvent(AnalyticCategories.Visits, `Opened: Messaging case (${data.completed ? 'completed' : 'ongoing'})`);
	}
}
